import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  formatAmount,
  formatDateWithYear,
} from "../../../../utils/hooks/dataHandler";
import { basisPeriod } from "../domain/preparation";

const numberValue = (value) => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const PDF_COLORS = {
  black: [0, 0, 0],
  headerFill: [242, 240, 248],
  transferFill: [249, 246, 252],
  alternateFill: [252, 252, 253],
  totalFill: [242, 240, 248],
};

const sum = (values) =>
  values.reduce((total, value) => total + numberValue(value), 0);

const companyLabel = (company) =>
  `${company?.name || "Unknown company"}${company?.isExternal ? " (EXTERNAL)" : ""}`;

const adjustmentTotals = (adjustment = {}) => ({
  transferred:
    Math.max(0, numberValue(adjustment.caTransfer)) +
    Math.max(0, numberValue(adjustment.lossesTransfer)) +
    Math.max(0, numberValue(adjustment.donationsTransfer)),
  claimed: Math.abs(
    Math.min(0, numberValue(adjustment.caTransfer)) +
      Math.min(0, numberValue(adjustment.lossesTransfer)) +
      Math.min(0, numberValue(adjustment.donationsTransfer)),
  ),
});

export const buildGroupReliefExportData = (result, detailedRows = []) => {
  const companies = result?.companies || [];
  const companyById = new Map(
    companies.map((company) => [company.id, company]),
  );
  const companyResults = companies.map((company, index) => {
    const adjustment = adjustmentTotals(result?.adjustments?.[company.id]);
    const beforeComputation = result?.preComputations?.[index] || {};
    const afterComputation = result?.postComputations?.[index] || {};
    const before = numberValue(
      beforeComputation.taxPayableBeforeEci ?? beforeComputation.taxPayable,
    );
    const after = numberValue(
      afterComputation.taxPayableBeforeEci ?? afterComputation.taxPayable,
    );
    return {
      company,
      companyName: companyLabel(company),
      before,
      transferred: adjustment.transferred,
      claimed: adjustment.claimed,
      after,
      saving: before - after,
    };
  });
  const totalTransfer = sum(companyResults.map((row) => row.transferred));
  const totalClaim = sum(companyResults.map((row) => row.claimed));
  const totalSaving = sum(companyResults.map((row) => row.saving));
  const transfers = (result?.transfers || []).map((transfer, index) => ({
    order: index + 1,
    transferor: companyLabel(companyById.get(transfer.transferorId)),
    claimant: companyLabel(companyById.get(transfer.claimantId)),
    ca: numberValue(transfer.caTransferred),
    losses: numberValue(transfer.lossesTransferred),
    donations: numberValue(transfer.donationsTransferred),
    total: numberValue(transfer.totalTransferred),
    qualifyingDays: transfer.pairQualifyingDays ?? "",
  }));

  return {
    settings: result?.settings || {},
    summary: [
      [
        "Tax payable before ECI (before Group Relief)",
        numberValue(result?.beforeSummary?.taxPayableBeforeEci),
      ],
      [
        "Tax payable before ECI (after Group Relief)",
        numberValue(result?.afterSummary?.taxPayableBeforeEci),
      ],
      ["Tax savings before ECI", numberValue(result?.taxSavingBeforeEci)],
      ["Total transfer", totalTransfer],
      ["Total claim", totalClaim],
    ],
    companyResults,
    transfers,
    detailedRows,
    totals: { totalTransfer, totalClaim, totalSaving },
  };
};

const displayAmount = (value, decimals = false) => {
  const numeric = numberValue(value);
  if (numeric === 0) return "-";
  return formatAmount(numeric, !decimals, false, false, decimals);
};

const detailedDisplayValue = (value, row) => {
  if (row.className === "spacer-row") return "";
  if (row.numeric === false) return value || "-";
  return displayAmount(value, Boolean(row.decimals));
};

const detailedPdfCell = (content, row) => {
  const styles = {};
  if (row.className === "transfer-step-row") {
    styles.fontStyle = "bold";
    styles.fillColor = PDF_COLORS.transferFill;
  }
  if (row.id === "net-chargeable-income") {
    styles.fontStyle = "bold";
    styles.lineWidth = { left: 0.1, bottom: 0.8, right: 0.1, top: 0.1 };
    styles.lineColor = PDF_COLORS.black;
    styles.fillColor = PDF_COLORS.totalFill;
  }

  return Object.keys(styles).length ? { content, styles } : content;
};

const detailedExcelValue = (value, row) => {
  if (row.className === "spacer-row") return "";
  if (row.numeric === false) return value || "";
  return numberValue(value);
};

const metadataRows = (settings) => {
  const basis = basisPeriod(settings.commonFinancialYearEnd);
  return [
    ["Year of Assessment", settings.yearOfAssessment || ""],
    ["Financial year end", settings.commonFinancialYearEnd || ""],
    [
      "Basis period",
      basis ? `${basis.start} to ${basis.end} (${basis.days} days)` : "",
    ],
    ["CIT rebate rate", `${settings.rebatePercentage || 0}%`],
    ["CIT rebate cap (SGD)", numberValue(settings.rebateCap)],
    ["Currency", "SGD"],
  ];
};

const safeFilePart = (value) =>
  String(value || "group-relief")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "group-relief";

const exportFileBase = (settings) =>
  `group-relief-results-ya-${safeFilePart(settings.yearOfAssessment)}`;

const formatHeaderDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : formatDateWithYear(value);
};

const buildGroupReliefHeader = (doc, sectionTitle, settings) => {
  const leftX = 14;
  let y = 15;
  const basis = basisPeriod(settings.commonFinancialYearEnd);

  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Group Relief Optimizer", leftX, y);
  y += 5;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  if (settings.yearOfAssessment) {
    doc.text(`Year of Assessment: ${settings.yearOfAssessment}`, leftX, y);
    y += 5;
  }
  if (settings.commonFinancialYearEnd) {
    doc.text(
      `Financial year end: ${formatHeaderDate(settings.commonFinancialYearEnd)}`,
      leftX,
      y,
    );
    y += 5;
  }
  if (basis) {
    doc.text(
      `Basis period: ${formatHeaderDate(basis.start)} to ${formatHeaderDate(
        basis.end,
      )} (${basis.days} days)`,
      leftX,
      y,
    );
    y += 5;
  }
  doc.text(
    `CIT rebate: ${settings.rebatePercentage || 0}%  ·  Cap: SGD ${formatAmount(
      settings.rebateCap || 0,
    )}`,
    leftX,
    y,
  );
  y += 5;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(sectionTitle, leftX, y + 2);
  return y + 8;
};

const cleanDetailedRows = (rows = []) => {
  return rows.filter((row) => {
    if (row.id === "company-name") return false;
    if (row.className === "spacer-row") return false;
    return Boolean(String(row.label || "").trim());
  });
};

const splitDetailedCompanies = (companies) => {
  if (companies.length <= 5) return [companies];
  const chunkSize = Math.min(5, Math.ceil(companies.length / 2));
  const chunks = [];
  for (let index = 0; index < companies.length; index += chunkSize) {
    chunks.push(companies.slice(index, index + chunkSize));
  }
  return chunks;
};

const detailedColumnStyles = (doc, companyCount) => {
  const availableWidth = doc.internal.pageSize.getWidth() - 28;
  const companyWidth = Math.min(
    44,
    Math.max(30, availableWidth / Math.max(companyCount + 2, 3)),
  );

  return {
    0: {
      minCellWidth: 65,
      halign: "left",
    },
    ...Object.fromEntries(
      Array.from({ length: companyCount }, (_, index) => [
        index + 1,
        { cellWidth: companyWidth, halign: "right" },
      ]),
    ),
  };
};

export const exportGroupReliefExcel = (result, detailedRows = []) => {
  const data = buildGroupReliefExportData(result, detailedRows);
  const workbook = XLSX.utils.book_new();
  const settings = data.settings;
  const exportDetailedRows = cleanDetailedRows(data.detailedRows);

  const resultRows = [
    ["Group Relief Results"],
    ...metadataRows(settings),
    [],
    ["Summary", "Amount (SGD)"],
    ...data.summary,
    [],
    ["Company Results"],
    [
      "Company",
      "Before Tax",
      "GR Transferred",
      "GR Claimed",
      "After Tax",
      "Saving",
    ],
    ...data.companyResults.map((row) => [
      row.companyName,
      row.before,
      row.transferred,
      row.claimed,
      row.after,
      row.saving,
    ]),
    [
      "Total",
      "",
      data.totals.totalTransfer,
      data.totals.totalClaim,
      "",
      data.totals.totalSaving,
    ],
  ];
  const transferRows = [
    ["Recommended Transfers"],
    ...metadataRows(settings),
    [],
    [
      "Order",
      "Transferor",
      "Claimant",
      "CA",
      "Trade losses",
      "Donations",
      "Total",
      "Qualifying days",
    ],
    ...data.transfers.map((row) => [
      row.order,
      row.transferor,
      row.claimant,
      row.ca,
      row.losses,
      row.donations,
      row.total,
      row.qualifyingDays,
    ]),
  ];
  if (!data.transfers.length) transferRows.push(["No transfers recommended."]);

  const detailedSheet = [
    ["Detailed Computation Output"],
    ...metadataRows(settings),
    [],
    ["Description", ...data.companyResults.map((row) => row.companyName)],
    ...exportDetailedRows.map((row) => [
      row.label,
      ...row.values.map((value) => detailedExcelValue(value, row)),
    ]),
  ];

  const appendSheet = (name, rows, widths) => {
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet["!cols"] = widths.map((width) => ({ wch: width }));
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
  };
  appendSheet("Group Relief Results", resultRows, [38, 18, 18, 18, 18, 18]);
  appendSheet(
    "Recommended Transfers",
    transferRows,
    [10, 28, 28, 16, 16, 16, 16, 18],
  );
  appendSheet("Detailed Computation", detailedSheet, [
    48,
    ...data.companyResults.map(() => 22),
  ]);
  XLSX.writeFile(workbook, `${exportFileBase(settings)}.xlsx`);
};

const pdfAmount = (value) => displayAmount(value, false);

const addPdfSectionPage = (doc, title, settings) => {
  doc.addPage();
  return buildGroupReliefHeader(doc, title, settings);
};

const pdfTableOptions = {
  theme: "grid",
  headStyles: {
    fillColor: PDF_COLORS.headerFill,
    textColor: PDF_COLORS.black,
    fontStyle: "bold",
    lineColor: PDF_COLORS.black,
    lineWidth: 0.1,
  },
  styles: {
    fontSize: 8,
    cellPadding: 2,
    overflow: "linebreak",
    textColor: PDF_COLORS.black,
    lineColor: PDF_COLORS.black,
    lineWidth: 0.1,
    valign: "middle",
  },
  alternateRowStyles: { fillColor: PDF_COLORS.alternateFill },
};

export const exportGroupReliefPdf = (result, detailedRows = []) => {
  const data = buildGroupReliefExportData(result, detailedRows);
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const settings = data.settings;
  const exportDetailedRows = cleanDetailedRows(data.detailedRows);

  const groupResultsStartY = buildGroupReliefHeader(
    doc,
    "Group Relief Results",
    settings,
  );
  autoTable(doc, {
    ...pdfTableOptions,
    startY: groupResultsStartY,
    head: [["Summary", "Amount (SGD)"]],
    body: data.summary.map(([label, value]) => [label, pdfAmount(value)]),
    columnStyles: {
      0: { cellWidth: 105 },
      1: { halign: "right", cellWidth: 45 },
    },
    didParseCell: (hookData) => {
      if (hookData.section === "head" && hookData.column.index > 0) {
        hookData.cell.styles.halign = "right";
      }
    },
  });
  autoTable(doc, {
    ...pdfTableOptions,
    startY: doc.lastAutoTable.finalY + 9,
    head: [
      [
        "Company",
        "Before Tax",
        "GR Transferred",
        "GR Claimed",
        "After Tax",
        "Saving",
      ],
    ],
    body: data.companyResults.map((row) => [
      row.companyName,
      pdfAmount(row.before),
      pdfAmount(row.transferred),
      pdfAmount(row.claimed),
      pdfAmount(row.after),
      pdfAmount(row.saving),
    ]),
    foot: [
      [
        "Total",
        "",
        pdfAmount(data.totals.totalTransfer),
        pdfAmount(data.totals.totalClaim),
        "",
        pdfAmount(data.totals.totalSaving),
      ],
    ],
    footStyles: {
      fontStyle: "bold",
      fillColor: PDF_COLORS.totalFill,
      textColor: PDF_COLORS.black,
      lineColor: PDF_COLORS.black,
      lineWidth: 0.1,
    },
    didParseCell: (hookData) => {
      if (hookData.section === "foot") {
        hookData.cell.styles.halign =
          hookData.column.index === 0 ? "left" : "right";
      }

      if (hookData.section === "head" && hookData.column.index > 0) {
        hookData.cell.styles.halign = "right";
      }
    },
    columnStyles: {
      0: { cellWidth: 65 },
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
    },
  });

  const transfersStartY = addPdfSectionPage(
    doc,
    "Recommended Transfers",
    settings,
  );
  autoTable(doc, {
    ...pdfTableOptions,
    startY: transfersStartY,
    head: [
      [
        "Order",
        "Transferor",
        "Claimant",
        "CA",
        "Trade losses",
        "Donations",
        "Total",
        "Qualifying days",
      ],
    ],
    body: data.transfers.length
      ? data.transfers.map((row) => [
          row.order,
          row.transferor,
          row.claimant,
          pdfAmount(row.ca),
          pdfAmount(row.losses),
          pdfAmount(row.donations),
          pdfAmount(row.total),
          row.qualifyingDays,
        ])
      : [["No transfers recommended.", "", "", "", "", "", "", ""]],
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 50 },
      2: { cellWidth: 50 },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
      6: { halign: "right", fontStyle: "bold" },
      7: { halign: "right" },
    },
    didParseCell: (hookData) => {
      if (hookData.section === "head" && hookData.column.index > 2) {
        hookData.cell.styles.halign = "right";
      }
    },
  });

  const detailedCompanyGroups = splitDetailedCompanies(data.companyResults);
  detailedCompanyGroups.forEach((companyGroup, groupIndex) => {
    const detailedStartY = addPdfSectionPage(
      doc,
      groupIndex === 0
        ? "Detailed Computation Output"
        : `Detailed Computation Output (${groupIndex + 1}/${detailedCompanyGroups.length})`,
      settings,
    );
    const companyIndexes = companyGroup.map((company) =>
      data.companyResults.indexOf(company),
    );
    autoTable(doc, {
      ...pdfTableOptions,
      startY: detailedStartY,
      tableWidth: doc.internal.pageSize.getWidth() - 28,
      head: [["Description", ...companyGroup.map((row) => row.companyName)]],
      body: exportDetailedRows.map((row) => [
        detailedPdfCell(row.label, row),
        ...companyIndexes.map((index) =>
          detailedPdfCell(detailedDisplayValue(row.values[index], row), row),
        ),
      ]),
      styles: { ...pdfTableOptions.styles, fontSize: 6.5, cellPadding: 1.5 },
      columnStyles: detailedColumnStyles(doc, companyGroup.length),
    });
  });

  doc.save(`${exportFileBase(settings)}.pdf`);
};
